import { create } from 'zustand';
import {
  applyOps,
  bboxOfItems,
  contentBBox,
  emptyDoc,
  invertOps,
  rectContainsCenter,
  rectsOverlap,
  strokeBBox,
  type BoardDoc,
  type Camera,
  type Comment,
  type GroupChild,
  type GroupItem,
  type Item,
  type Op,
  type Peer,
  type Point,
  type Rect,
  type ServerMsg,
  type Stroke,
  type Txn,
} from '@scribbleplot/shared';
import { newId } from '../lib/id';
import { CANVAS, STICKY_PALETTE } from '../tokens';
import { coalesceOps } from './coalesce';
import { markStep } from './onboarding';
import { BoardSocket, type SocketStatus } from './socket';
import { saveSandbox } from './sandbox';

/**
 * Board state.
 *
 * Edits are expressed as ops (see `@scribbleplot/shared`), applied optimistically
 * and forwarded to the server, which assigns their order and echoes them to
 * every peer. Undo/redo replays inverse ops, so it works the same whether the
 * change came from a keystroke or a drag.
 */

export type Tool = 'select' | 'hand' | 'sticky' | 'text' | 'frame' | 'draw' | 'connect' | 'link' | 'comment';

export type MenuState = {
  /** Screen coordinates for placement. */
  sx: number;
  sy: number;
  /** Target entity, or null for the canvas background menu. */
  id: string | null;
  /** World coordinates, for menu actions that insert at the click point. */
  wx: number;
  wy: number;
  isStroke?: boolean;
};

const HISTORY_LIMIT = 100;

type Gesture = { snapshot: BoardDoc; ops: Op[] } | null;

export type BoardState = {
  boardId: string | null;
  slug: string;
  name: string;
  /** Local-only demo board: no session, no socket, nothing written server-side. */
  sandbox: boolean;
  canEdit: boolean;
  /** Owner-only rights: sharing, invites, deletion. */
  canManage: boolean;
  role: 'owner' | 'editor' | 'viewer';
  isPublic: boolean;
  status: SocketStatus | 'loading';
  pendingCount: number;
  gesturing: boolean;

  doc: BoardDoc;
  rev: number;

  sel: string[];
  tool: Tool;
  editing: string | null;
  menu: MenuState | null;

  marquee: Rect | null;
  frameDraft: Rect | null;
  drawPts: Point[] | null;
  connectFrom: string | null;
  connLive: Point | null;

  past: Txn[];
  future: Txn[];

  peers: Peer[];
  selfId: string | null;

  /** Set when the server refuses an edit, so the UI can explain why. */
  lastError: string | null;
};

type Actions = {
  attach: (params: {
    boardId: string;
    slug: string;
    name: string;
    isPublic: boolean;
    canEdit: boolean;
    canManage: boolean;
    role: 'owner' | 'editor' | 'viewer';
    doc: BoardDoc;
    rev: number;
  }) => void;
  connect: () => void;
  /** Open a board that exists only in this browser. */
  attachSandbox: (name: string, doc: BoardDoc) => void;
  detach: () => void;
  handleServerMessage: (msg: ServerMsg) => void;
  setStatus: (status: SocketStatus) => void;

  /* editing primitives */
  commit: (ops: Op[]) => void;
  /** Apply and sync without recording an undo step. */
  applySilent: (ops: Op[]) => void;
  beginGesture: () => void;
  gestureOps: (ops: Op[]) => void;
  endGesture: () => void;
  undo: () => void;
  redo: () => void;

  /* selection & tools */
  setTool: (tool: Tool) => void;
  setSel: (ids: string[]) => void;
  toggleSel: (id: string) => void;
  selectAll: () => void;
  setEditing: (id: string | null) => void;
  setMenu: (menu: MenuState | null) => void;
  setMarquee: (rect: Rect | null) => void;
  setFrameDraft: (rect: Rect | null) => void;
  setDrawPts: (pts: Point[] | null) => void;
  setConnect: (from: string | null, live: Point | null) => void;
  clearOverlays: () => void;

  /* item operations */
  addSticky: (wx: number, wy: number) => string;
  addText: (wx: number, wy: number) => string;
  addLink: (wx: number, wy: number) => string;
  addImage: (src: string, nw: number, nh: number, wx: number, wy: number, name: string, assetId?: string) => string;
  addFrame: (rect: Rect) => string;
  addStroke: (pts: Point[], color: string) => void;
  addConnector: (a: string, b: string) => void;
  addComment: (wx: number, wy: number, author: string) => string;
  addCommentMsg: (commentId: string, who: string, text: string) => void;
  resolveComment: (commentId: string) => void;

  updateItem: (id: string, patch: Partial<Item>) => void;
  renameBoard: (name: string) => void;
  groupSel: () => boolean;
  ungroup: (id: string) => void;
  duplicateSel: () => void;
  deleteSel: () => void;
  bringToFront: () => void;
  sendToBack: () => void;

  /* lookups */
  itemById: (id: string) => Item | undefined;
  strokeById: (id: string) => Stroke | undefined;
  nextZ: () => number;
  nextFrameZ: () => number;
};

export type BoardStore = BoardState & Actions;

let socket: BoardSocket | null = null;
let gesture: Gesture = null;

/**
 * Network throttle for gesture traffic.
 *
 * A drag or resize produces an op per pointer frame — up to 120 a second. Those
 * are applied locally straight away so the canvas stays responsive, but they are
 * batched before going out: peers do not need every intermediate frame, and
 * coalescing keeps the final position exact.
 */
const GESTURE_FLUSH_MS = 33;
let outbox: Op[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function flushOutbox(): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!outbox.length) return;
  const batch = coalesceOps(outbox);
  outbox = [];
  socket?.sendOps(batch);
}

function queueOps(ops: Op[]): void {
  outbox.push(...ops);
  if (!flushTimer) flushTimer = setTimeout(flushOutbox, GESTURE_FLUSH_MS);
}

const initial: BoardState = {
  boardId: null,
  slug: '',
  name: '',
  sandbox: false,
  canEdit: false,
  canManage: false,
  role: 'viewer',
  isPublic: false,
  status: 'loading',
  doc: emptyDoc(),
  rev: 0,
  sel: [],
  tool: 'select',
  editing: null,
  menu: null,
  marquee: null,
  frameDraft: null,
  drawPts: null,
  connectFrom: null,
  connLive: null,
  past: [],
  future: [],
  peers: [],
  selfId: null,
  lastError: null,
  pendingCount: 0,
  gesturing: false,
};

export const useBoard = create<BoardStore>((set, get) => {
  /**
   * Apply ops locally and push them to the server.
   *
   * `mode` decides how the change is treated:
   *   - 'commit'  — a user edit; recorded in history and sent immediately
   *   - 'gesture' — a pointer frame; sent batched, history handled by endGesture
   *   - 'silent'  — bookkeeping (raising z on click); sent, never in history
   */
  const dispatch = (ops: Op[], mode: 'commit' | 'gesture' | 'silent') => {
    const state = get();
    if (!state.canEdit || !ops.length) return;

    const nameOp = ops.find((op) => op.t === 'name');
    const patch: Partial<BoardState> = { doc: applyOps(state.doc, ops) };
    if (nameOp && nameOp.t === 'name') patch.name = nameOp.name;

    if (mode === 'commit') {
      const undo = coalesceOps(invertOps(state.doc, ops, state.name));
      if (undo.length) {
        patch.past = [...state.past, { do: coalesceOps(ops), undo }].slice(-HISTORY_LIMIT);
        patch.future = [];
      }
    }

    set(patch);

    if (state.sandbox) {
      // Nothing to send: the demo board is this browser's alone.
      if (patch.doc) saveSandbox(patch.doc);
      return;
    }

    if (mode === 'gesture') {
      queueOps(ops);
    } else {
      // Anything already queued must go first, or the server would apply this
      // op before the frames that preceded it.
      flushOutbox();
      socket?.sendOps(ops);
    }
  };

  return {
    ...initial,

    attach: ({ boardId, slug, name, isPublic, canEdit, canManage, role, doc, rev }) => {
      set({
        ...initial,
        boardId,
        slug,
        name,
        isPublic,
        canEdit,
        canManage,
        role,
        doc,
        rev,
        status: 'loading',
      });
    },

    attachSandbox: (name, doc) => {
      socket?.close();
      socket = null;
      set({
        ...initial,
        boardId: 'demo',
        slug: 'demo',
        name,
        sandbox: true,
        canEdit: true,
        // Sharing and collaborators need an account behind them.
        canManage: false,
        role: 'owner',
        doc,
        rev: 0,
        status: 'live',
      });
    },

    connect: () => {
      const { boardId } = get();
      if (!boardId) return;
      socket?.close();
      socket = new BoardSocket(boardId, {
        onMessage: (msg) => get().handleServerMessage(msg),
        onStatus: (status) => get().setStatus(status),
        onPending: (pendingCount) => set({ pendingCount }),
      });
      socket.connect();
    },

    detach: () => {
      if (flushTimer) clearTimeout(flushTimer);
      flushTimer = null;
      outbox = [];
      socket?.close();
      socket = null;
      gesture = null;
      set({ ...initial });
    },

    setStatus: (status) => set({ status }),

    handleServerMessage: (msg) => {
      const state = get();
      switch (msg.t) {
        case 'hello':
          set({
            selfId: msg.self.id,
            rev: msg.rev,
            doc: msg.doc,
            name: msg.name,
            canEdit: msg.canEdit,
            peers: msg.peers,
            status: 'live',
            // The server's document is authoritative on (re)connect, which makes
            // any local history stale — replaying it could resurrect deletions.
            past: [],
            future: [],
          });
          break;

        case 'ops':
          // Our own ops were already applied optimistically; just advance rev.
          if (msg.origin === state.selfId) {
            set({ rev: msg.rev });
          } else {
            set({ rev: msg.rev, doc: applyOps(state.doc, msg.ops) });
          }
          break;

        case 'name':
          set({ name: msg.name });
          break;

        case 'access':
          /**
           * Rights changed under us. Drop history along with the change: a
           * demoted viewer must not be able to replay edits, and a promotion
           * arrives with a fresh `sync` anyway.
           */
          set({
            canEdit: msg.canEdit,
            role: msg.role,
            canManage: msg.role === 'owner',
            past: [],
            future: [],
            editing: null,
            menu: null,
            tool: 'select',
          });
          break;

        case 'peers':
          set({ peers: msg.peers.filter((peer) => peer.id !== state.selfId) });
          break;

        case 'cursor':
          set({
            peers: state.peers.map((peer) => (peer.id === msg.id ? { ...peer, cursor: msg.pos } : peer)),
          });
          break;

        case 'sync':
          set({ rev: msg.rev, doc: msg.doc, name: msg.name, past: [], future: [] });
          break;

        case 'reject':
          set({ lastError: msg.reason });
          socket?.requestSync();
          break;

        case 'error':
          set({ lastError: msg.message });
          break;

        case 'pong':
          break;
      }
    },

    /* ---------------- editing primitives ---------------- */

    commit: (ops) => dispatch(ops, 'commit'),
    applySilent: (ops) => dispatch(ops, 'silent'),

    beginGesture: () => {
      set({ gesturing: true });
      gesture = { snapshot: get().doc, ops: [] };
    },

    gestureOps: (ops) => {
      if (gesture) gesture.ops.push(...ops);
      dispatch(ops, 'gesture');
    },

    endGesture: () => {
      set({ gesturing: false });
      // Make sure the last frames of the gesture are on the wire before the
      // next op (which flushes eagerly) can overtake them.
      flushOutbox();
      const active = gesture;
      gesture = null;
      if (!active || !active.ops.length) return;
      // One history entry for the whole gesture, not one per pointer frame.
      const undo = coalesceOps(invertOps(active.snapshot, active.ops, get().name));
      if (!undo.length) return;
      set((state) => ({
        past: [...state.past, { do: coalesceOps(active.ops), undo }].slice(-HISTORY_LIMIT),
        future: [],
      }));
    },

    undo: () => {
      const state = get();
      if (!state.canEdit) return;
      const entry = state.past[state.past.length - 1];
      if (!entry) return;
      set({
        doc: applyOps(state.doc, entry.undo),
        past: state.past.slice(0, -1),
        future: [...state.future, entry].slice(-HISTORY_LIMIT),
        sel: [],
        editing: null,
        menu: null,
      });
      const renamed = entry.undo.find((op) => op.t === 'name');
      if (renamed && renamed.t === 'name') set({ name: renamed.name });
      if (state.sandbox) saveSandbox(get().doc);
      else socket?.sendOps(entry.undo);
    },

    redo: () => {
      const state = get();
      if (!state.canEdit) return;
      const entry = state.future[state.future.length - 1];
      if (!entry) return;
      set({
        doc: applyOps(state.doc, entry.do),
        future: state.future.slice(0, -1),
        past: [...state.past, entry].slice(-HISTORY_LIMIT),
        sel: [],
        editing: null,
        menu: null,
      });
      const renamed = entry.do.find((op) => op.t === 'name');
      if (renamed && renamed.t === 'name') set({ name: renamed.name });
      if (state.sandbox) saveSandbox(get().doc);
      else socket?.sendOps(entry.do);
    },

    /* ---------------- selection & tools ---------------- */

    setTool: (tool) => set({ tool, connectFrom: null, connLive: null, menu: null, editing: null }),
    setSel: (sel) => set({ sel }),
    toggleSel: (id) =>
      set((state) => ({
        sel: state.sel.includes(id) ? state.sel.filter((s) => s !== id) : [...state.sel, id],
      })),
    selectAll: () =>
      set((state) => ({
        sel: [...state.doc.items.map((i) => i.id), ...state.doc.strokes.map((s) => s.id)],
      })),
    setEditing: (editing) => set({ editing, menu: null }),
    setMenu: (menu) => set({ menu }),
    setMarquee: (marquee) => set({ marquee }),
    setFrameDraft: (frameDraft) => set({ frameDraft }),
    setDrawPts: (drawPts) => set({ drawPts }),
    setConnect: (connectFrom, connLive) => set({ connectFrom, connLive }),
    clearOverlays: () =>
      set({ menu: null, editing: null, marquee: null, frameDraft: null, connectFrom: null, connLive: null }),

    /* ---------------- lookups ---------------- */

    itemById: (id) => get().doc.items.find((it) => it.id === id),
    strokeById: (id) => get().doc.strokes.find((s) => s.id === id),

    /** Highest z among ordinary items, so new items land on top. */
    nextZ: () => {
      const items = get().doc.items.filter((it) => it.type !== 'frame');
      return items.reduce((max, it) => Math.max(max, it.z), 1000) + 1;
    },
    /** Frames sit in their own band beneath everything else. */
    nextFrameZ: () => {
      const frames = get().doc.items.filter((it) => it.type === 'frame');
      return frames.reduce((max, it) => Math.max(max, it.z), 0) + 1;
    },

    /* ---------------- item operations ---------------- */

    addSticky: (wx, wy) => {
      const id = newId();
      const color = STICKY_PALETTE[Math.floor(Math.random() * STICKY_PALETTE.length)];
      const item: Item = {
        id,
        type: 'sticky',
        x: wx - 90,
        y: wy - 90,
        w: 180,
        h: 180,
        color,
        text: '',
        z: get().nextZ(),
      };
      get().commit([{ t: 'add', c: 'items', v: [item] }]);
      set({ sel: [id], editing: id, tool: 'select' });
      markStep('note');
      return id;
    },

    addText: (wx, wy) => {
      const id = newId();
      const item: Item = {
        id,
        type: 'text',
        x: wx - 20,
        y: wy - 20,
        w: 340,
        h: 100,
        size: 'm',
        text: '',
        z: get().nextZ(),
      };
      get().commit([{ t: 'add', c: 'items', v: [item] }]);
      set({ sel: [id], editing: id, tool: 'select' });
      markStep('note');
      return id;
    },

    addLink: (wx, wy) => {
      const id = newId();
      const item: Item = {
        id,
        type: 'link',
        x: wx - 120,
        y: wy - 44,
        w: 240,
        h: 84,
        title: 'Link card',
        url: 'example.com',
        z: get().nextZ(),
      };
      get().commit([{ t: 'add', c: 'items', v: [item] }]);
      set({ sel: [id], editing: id, tool: 'select' });
      return id;
    },

    addImage: (src, nw, nh, wx, wy, name, assetId) => {
      // Scale to a sane on-canvas size while preserving aspect ratio.
      const scale = Math.min(1, CANVAS.imageMaxEdge / Math.max(nw || 320, nh || 240));
      const w = Math.max(80, (nw || 320) * scale);
      const h = Math.max(60, (nh || 240) * scale);
      const id = newId();
      const item: Item = {
        id,
        type: 'image',
        x: wx - w / 2,
        y: wy - h / 2,
        w,
        h,
        src,
        name: name || 'image',
        z: get().nextZ(),
        ...(assetId ? { assetId } : {}),
      };
      get().commit([{ t: 'add', c: 'items', v: [item] }]);
      set({ sel: [id] });
      markStep('image');
      return id;
    },

    addFrame: (rect) => {
      const id = newId();
      const item: Item = {
        id,
        type: 'frame',
        x: rect.x,
        y: rect.y,
        w: rect.w,
        h: rect.h,
        label: 'Area',
        z: get().nextFrameZ(),
      };
      get().commit([{ t: 'add', c: 'items', v: [item] }]);
      set({ sel: [id], tool: 'select', frameDraft: null });
      return id;
    },

    addStroke: (pts, color) => {
      const stroke: Stroke = { id: newId(), pts, color, w: 2.5 };
      get().commit([{ t: 'add', c: 'strokes', v: [stroke] }]);
    },

    addConnector: (a, b) => {
      if (a === b) return;
      // Do not stack duplicate links between the same pair.
      const exists = get().doc.conns.some((c) => (c.a === a && c.b === b) || (c.a === b && c.b === a));
      if (exists) return;
      get().commit([{ t: 'add', c: 'conns', v: [{ id: newId(), a, b }] }]);
    },

    addComment: (wx, wy, author) => {
      const id = newId();
      const comment: Comment = { id, x: wx, y: wy, author, msgs: [] };
      get().commit([{ t: 'add', c: 'comments', v: [comment] }]);
      set({ tool: 'select' });
      return id;
    },

    addCommentMsg: (commentId, who, text) => {
      get().commit([{ t: 'msg', id: commentId, msg: { id: newId(), who, text, at: Date.now() } }]);
    },

    resolveComment: (commentId) => {
      get().commit([{ t: 'del', c: 'comments', ids: [commentId] }]);
    },

    updateItem: (id, patch) => {
      get().commit([{ t: 'set', c: 'items', p: [{ id, patch: patch as Record<string, unknown> }] }]);
    },

    renameBoard: (name) => {
      const trimmed = name.trim();
      if (!trimmed || trimmed === get().name) return;
      if (get().sandbox) {
        set({ name: trimmed });
        return;
      }
      get().commit([{ t: 'name', name: trimmed }]);
    },

    /**
     * Fold the selection into a single labelled chip.
     *
     * Children keep coordinates relative to the group so the whole cluster moves
     * as one, and expanding later restores the original layout exactly.
     */
    groupSel: () => {
      const state = get();
      const ids = state.sel.filter((id) => {
        const item = state.itemById(id);
        return item && item.type !== 'frame' && item.type !== 'group';
      });
      if (ids.length < 2) return false;

      const box = bboxOfItems(ids, state.doc.items);
      if (!box) return false;

      const gx = box.x + box.w / 2 - 100;
      const gy = box.y + box.h / 2 - 38;
      const members = state.doc.items.filter((it) => ids.includes(it.id));

      const children: GroupChild[] = members.map((it) => {
        const base = { id: it.id, rx: it.x - gx, ry: it.y - gy, w: it.w, h: it.h };
        switch (it.type) {
          case 'sticky':
            return { ...base, type: 'sticky', color: it.color, text: it.text, textStyle: it.textStyle };
          case 'text':
            return { ...base, type: 'text', text: it.text, size: it.size, textStyle: it.textStyle };
          case 'image':
            return { ...base, type: 'image', src: it.src, name: it.name, assetId: it.assetId };
          case 'link':
            return { ...base, type: 'link', title: it.title, url: it.url };
          default:
            return { ...base, type: 'sticky', color: STICKY_PALETTE[0] };
        }
      });

      const group: GroupItem = {
        id: newId(),
        type: 'group',
        x: gx,
        y: gy,
        w: 200,
        h: 76,
        name: 'Group',
        children,
        z: state.nextZ(),
      };

      // Connectors survive: they resolve a grouped child to its group.
      get().commit([
        { t: 'del', c: 'items', ids },
        { t: 'add', c: 'items', v: [group] },
      ]);
      set({ sel: [group.id] });
      markStep('group');
      return true;
    },

    ungroup: (id) => {
      const state = get();
      const group = state.itemById(id);
      if (!group || group.type !== 'group') return;

      let z = state.nextZ();
      const restored: Item[] = group.children.map((child) => {
        const base = { id: child.id, x: group.x + child.rx, y: group.y + child.ry, w: child.w, h: child.h, z: z++ };
        switch (child.type) {
          case 'text':
            return {
              ...base,
              type: 'text',
              text: child.text ?? '',
              size: child.size ?? 'm',
              textStyle: child.textStyle,
            };
          case 'image':
            return {
              ...base,
              type: 'image',
              src: child.src ?? '',
              name: child.name ?? 'image',
              ...(child.assetId ? { assetId: child.assetId } : {}),
            };
          case 'link':
            return { ...base, type: 'link', title: child.title ?? 'Link', url: child.url ?? '' };
          case 'sticky':
          default:
            return {
              ...base,
              type: 'sticky',
              color: child.color ?? STICKY_PALETTE[0],
              text: child.text ?? '',
              textStyle: child.textStyle,
            };
        }
      });

      get().commit([
        { t: 'del', c: 'items', ids: [id] },
        { t: 'add', c: 'items', v: restored },
      ]);
      set({ sel: restored.map((it) => it.id) });
    },

    duplicateSel: () => {
      const state = get();
      if (!state.sel.length) return;

      const items: Item[] = [];
      const strokes: Stroke[] = [];
      let z = state.nextZ();

      for (const id of state.sel) {
        const item = state.itemById(id);
        if (item) {
          const copy = { ...item, id: newId(), x: item.x + 28, y: item.y + 28, z: z++ } as Item;
          // Children need fresh ids too, or the clone would alias the original.
          if (copy.type === 'group') {
            copy.children = copy.children.map((child) => ({ ...child, id: newId() }));
          }
          items.push(copy);
          continue;
        }
        const stroke = state.strokeById(id);
        if (stroke) {
          strokes.push({ ...stroke, id: newId(), pts: stroke.pts.map(([x, y]) => [x + 28, y + 28] as Point) });
        }
      }

      const ops: Op[] = [];
      if (items.length) ops.push({ t: 'add', c: 'items', v: items });
      if (strokes.length) ops.push({ t: 'add', c: 'strokes', v: strokes });
      if (!ops.length) return;

      get().commit(ops);
      set({ sel: [...items.map((i) => i.id), ...strokes.map((s) => s.id)] });
    },

    deleteSel: () => {
      const state = get();
      if (!state.sel.length) return;
      const selected = new Set(state.sel);

      const itemIds = state.doc.items.filter((it) => selected.has(it.id)).map((it) => it.id);
      const strokeIds = state.doc.strokes.filter((s) => selected.has(s.id)).map((s) => s.id);

      // Work out which ids disappear, including children of deleted groups, so
      // orphaned connectors go in the same transaction and undo restores both.
      const gone = new Set(itemIds);
      for (const it of state.doc.items) {
        if (it.type === 'group' && selected.has(it.id)) for (const child of it.children) gone.add(child.id);
      }
      const connIds = state.doc.conns.filter((c) => gone.has(c.a) || gone.has(c.b)).map((c) => c.id);

      const ops: Op[] = [];
      if (connIds.length) ops.push({ t: 'del', c: 'conns', ids: connIds });
      if (itemIds.length) ops.push({ t: 'del', c: 'items', ids: itemIds });
      if (strokeIds.length) ops.push({ t: 'del', c: 'strokes', ids: strokeIds });
      if (!ops.length) return;

      get().commit(ops);
      set({ sel: [], editing: null, menu: null });
    },

    bringToFront: () => {
      const state = get();
      let z = state.nextZ();
      let frameZ = state.nextFrameZ();
      const patches = state.doc.items
        .filter((it) => state.sel.includes(it.id))
        .map((it) => ({ id: it.id, patch: { z: it.type === 'frame' ? frameZ++ : z++ } }));
      if (patches.length) get().commit([{ t: 'set', c: 'items', p: patches }]);
    },

    sendToBack: () => {
      const state = get();
      const patches = state.doc.items
        .filter((it) => state.sel.includes(it.id))
        .map((it) => ({ id: it.id, patch: { z: it.type === 'frame' ? 1 : 1000 } }));
      if (patches.length) get().commit([{ t: 'set', c: 'items', p: patches }]);
    },
  };
});

/* ---------------- helpers used by the canvas ---------------- */

/**
 * Cursor updates, rate-limited.
 *
 * `pointermove` fires up to 120 times a second; peers render the cursor with a
 * ~110ms transition, so anything beyond ~20 updates a second is invisible
 * bandwidth. `null` (pointer left the canvas) is always sent immediately.
 */
const CURSOR_INTERVAL_MS = 50;
let lastCursorAt = 0;

export function sendCursor(pos: { x: number; y: number } | null): void {
  if (!pos) {
    lastCursorAt = 0;
    socket?.sendCursor(null);
    return;
  }
  const now = performance.now();
  if (now - lastCursorAt < CURSOR_INTERVAL_MS) return;
  lastCursorAt = now;
  socket?.sendCursor(pos);
}

/** Items and strokes whose bounds intersect a marquee rectangle. */
export function hitTestRect(doc: BoardDoc, rect: Rect): string[] {
  const hits: string[] = [];
  for (const it of doc.items) {
    // Frames are containers; a marquee should select what is inside them.
    if (it.type === 'frame') continue;
    if (rectsOverlap(it, rect)) hits.push(it.id);
  }
  for (const stroke of doc.strokes) {
    if (rectsOverlap(strokeBBox(stroke), rect)) hits.push(stroke.id);
  }
  return hits;
}

/** Entities whose centre falls inside a frame — they travel with it. */
export function contentsOfFrame(doc: BoardDoc, frame: Item): { items: string[]; strokes: string[] } {
  const items = doc.items
    .filter((it) => it.id !== frame.id && it.type !== 'frame' && rectContainsCenter(frame, it))
    .map((it) => it.id);
  const strokes = doc.strokes.filter((s) => rectContainsCenter(frame, strokeBBox(s))).map((s) => s.id);
  return { items, strokes };
}

export { contentBBox };
